import { getMatchWithColors, getNextBasePath, normalizePossessionStatistics, PUBLIC_MATCH_OPERATIONS_V2_MEDIA_TYPE, type PublicMatchOperationSnapshotV2, type PublicMatchOperationEventV2 } from '@y-sports/api-client';

export type PlayerStats = { participantId: string; teamSide: string; attempts: number; onTarget: number; offTarget: number; goals: number; shotsFaced: number; saves: number; posts?: number; blockedShots?: number; turnovers?: number; technicalFaults?: number; lostBalls?: number };
export type PublicEvent = PublicMatchOperationEventV2 & { shotOutcome?: string | null; shotType?: string | null; goalkeeperParticipantId?: string | null; defenderParticipantId?: string | null };
export type CenterSnapshot = PublicMatchOperationSnapshotV2 & { state: PublicMatchOperationSnapshotV2['state'] & { participantStatistics?: PlayerStats[] } };
export type CenterData = { snapshot: CenterSnapshot; events: PublicEvent[]; receivedAt: number };
const url = (id: string, path: string) => `${getNextBasePath()}/public/matches/${encodeURIComponent(id)}/operations/${path}`;
async function get<T>(id: string, path: string, signal: AbortSignal): Promise<T> {
  const requestAbort = new AbortController();
  const cancel = () => requestAbort.abort();
  signal.addEventListener('abort', cancel, { once: true });
  if (signal.aborted) cancel();
  const timeout = setTimeout(cancel, 10000);
  try {
    const response = await fetch(url(id, path), { signal: requestAbort.signal, headers: { Accept: PUBLIC_MATCH_OPERATIONS_V2_MEDIA_TYPE } });
    if (!response.ok) throw new Error(`Public match data unavailable (${response.status})`);
    return await response.json();
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener('abort', cancel);
  }
}
export const loadPublicMatch = getMatchWithColors;

export function effectiveEvents(events: PublicEvent[]): PublicEvent[] {
  const result = new Map<string, PublicEvent>();
  for (const event of [...events].sort((a, b) => a.revision - b.revision)) {
    if (event.type === 'EVENT_VOIDED' || event.type === 'EVENT_REPLACED') result.delete(event.targetEventId || '');
    else result.set(event.id, event.type === 'STEAL_RECORDED' ? { ...event, type: 'LOST_BALL_RECORDED' } : event);
  }
  return [...result.values()].sort((a, b) => b.revision - a.revision);
}

export async function loadCenter(id: string, signal: AbortSignal, previous?: CenterData): Promise<CenterData> {
  const snapshot = await get<CenterSnapshot>(id, 'snapshot', signal);
  snapshot.state.participantStatistics = snapshot.state.participantStatistics?.map(normalizePossessionStatistics);
  const receivedAt = Date.now();
  let cursor = previous?.snapshot.revision ?? 0;
  let events = previous?.events ?? [];
  if (cursor > snapshot.revision) { cursor = 0; events = []; }
  while (cursor < snapshot.revision) {
    const page = await get<{ toRevision: number; events: PublicEvent[] }>(id, `events?afterRevision=${cursor}&limit=500`, signal);
    if (page.toRevision <= cursor) throw new Error('Incomplete public match history');
    events = effectiveEvents([...events, ...page.events.filter(event => event.revision <= snapshot.revision)]);
    cursor = page.toRevision;
  }
  return { snapshot, events, receivedAt };
}

export function subscribeCenter(id: string, revision: number, refresh: () => void, status: (connected: boolean) => void) {
  const source = new EventSource(url(id, `feed?afterRevision=${revision}`));
  source.onopen = () => { status(true); refresh(); };
  source.onerror = () => status(false); // Native EventSource reconnects; HTTP polling remains a fallback.
  source.addEventListener('operations-delta', refresh);
  source.addEventListener('snapshot-required', refresh);
  return () => source.close();
}
